import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import type { ReactNode } from "react";

import { cn } from "../cn.ts";
import { AlarmFill, GlowFill } from "../effects/shader-glow.tsx";
import { Icon } from "./icon.tsx";
import type { IconName } from "./icon.tsx";

// Feedback: status, progress, banners and empty states. Status tones always
// ship with a label (and a dot), never colour alone. The toast store and
// `<Toaster>` live in toast.tsx (size-ceilings).

export type Tone = "neutral" | "primary" | "success" | "warning" | "danger" | "info";

const DOT: Readonly<Record<Tone, string>> = {
  neutral: "bg-fg-subtle",
  primary: "bg-data",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  info: "bg-info",
};

type StatusDotProps = { readonly tone: Tone; readonly label: string; readonly live?: boolean };
const LIVE_DOT = "animate-pulse motion-reduce:animate-none";
/** A coloured dot and a label: "● Running"; `live` pulses it while the state changes by itself. */
export function StatusDot({ tone, label, live = false }: StatusDotProps) {
  return (
    <span className="inline-flex items-center gap-2 text-sm whitespace-nowrap text-fg">
      <span aria-hidden="true" className={cn("size-2 rounded-full", DOT[tone], live && LIVE_DOT)} />
      {label}
    </span>
  );
}

const pill = cva(
  "inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap [&_svg]:size-3",
  {
    variants: {
      tone: {
        neutral: "bg-bg-subtle text-fg-muted ring-1 ring-border",
        primary: "bg-primary-soft text-primary-soft-fg",
        success: "bg-success-soft text-success",
        warning: "bg-warning-soft text-warning-fg",
        danger: "bg-danger-soft text-danger",
        info: "bg-info-soft text-info",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

export function Pill({
  tone,
  icon,
  children,
}: VariantProps<typeof pill> & { readonly icon?: IconName; readonly children: ReactNode }) {
  return (
    <span className={pill({ tone })}>
      {icon ? <Icon name={icon} /> : null}
      {children}
    </span>
  );
}

const BAR: Readonly<Record<Tone, string>> = {
  neutral: "bg-fg-subtle",
  primary: "bg-data",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  info: "bg-info",
};

export type ProgressBarProps = {
  /** 0 to 100. */
  readonly value: number;
  readonly tone?: Tone;
  readonly label: string;
  readonly showValue?: boolean;
  readonly size?: "sm" | "md";
};

export function ProgressBar({ value, tone = "primary", label, showValue, size = "md" }: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div className="flex w-full items-center gap-3">
      <div
        role="progressbar"
        aria-label={label}
        aria-valuenow={Math.round(clamped)}
        aria-valuemin={0}
        aria-valuemax={100}
        className={cn("flex-1 overflow-hidden rounded-full bg-border", size === "sm" ? "h-1.5" : "h-2")}
      >
        <div
          className={cn("h-full rounded-full transition-[width] duration-500", BAR[tone])}
          style={{ width: `${clamped}%` }}
        />
      </div>
      {showValue ? (
        <span className="w-10 text-right text-xs font-semibold tabular-nums text-fg-muted">
          {Math.round(clamped)}%
        </span>
      ) : null}
    </div>
  );
}

/** A stacked bar of counts, each in its tone; for status distributions. */
export function DistributionBar({
  parts,
  label,
}: {
  readonly parts: ReadonlyArray<{ tone: Tone; value: number }>;
  readonly label: string;
}) {
  const total = parts.reduce((sum, part) => sum + part.value, 0);
  return (
    <div
      role="img"
      aria-label={label}
      className="flex h-2 w-full gap-0.5 overflow-hidden rounded-full bg-border"
    >
      {total === 0
        ? null
        : parts
            .filter((part) => part.value > 0)
            .map((part, index) => (
              <div
                key={index}
                className={cn("h-full", BAR[part.tone])}
                style={{ width: `${(part.value / total) * 100}%` }}
              />
            ))}
    </div>
  );
}

// Tones come in pairs, quiet and loud: caution / warning, danger / error.
// A loud banner fills with its colour, a little see-through over a blur,
// inside an edge of its own (--banner-edge). Everything in it is drawn in the
// banner's ink (--banner-ink): selected text, and the action, a quiet outlined
// button rather than a solid one, hover included, so a Button's own look
// cannot fight the banner.
const LOUD_ACTION = [
  "border-[1.5px] border-(--banner-edge) bg-(--banner-fill)/80 text-(--banner-ink) backdrop-blur-md",
  "selection:bg-(--banner-ink)/20 selection:text-(--banner-ink)",
  "[&_[data-slot=action]_:is(a,button)]:h-7 [&_[data-slot=action]_:is(a,button)]:px-2.5 [&_[data-slot=action]_:is(a,button)]:text-xs",
  "[&_[data-slot=action]_:is(a,button)]:border-(--banner-ink)/25 [&_[data-slot=action]_:is(a,button)]:bg-(--banner-ink)/8 [&_[data-slot=action]_:is(a,button)]:text-(--banner-ink)",
  "[&_[data-slot=action]_:is(a,button)]:shadow-none [&_[data-slot=action]_:is(a,button)]:hover:bg-(--banner-ink)/15",
].join(" ");

const banner = cva(
  "relative isolate flex flex-col gap-3 overflow-hidden rounded-lg px-4 py-3 sm:flex-row sm:items-center",
  {
    variants: {
      tone: {
        warning: [
          LOUD_ACTION,
          "[--banner-fill:var(--warning-solid)] [--banner-ink:var(--warning-solid-fg)] [--banner-edge:var(--warning-solid-edge)]",
        ].join(" "),
        primary: [
          LOUD_ACTION,
          "[--banner-fill:var(--inverted)] [--banner-ink:var(--inverted-fg)] [--banner-edge:var(--inverted-edge)]",
        ].join(" "),
        error: [
          LOUD_ACTION,
          "[--banner-fill:var(--danger-solid)] [--banner-ink:var(--danger-solid-fg)] [--banner-edge:var(--danger-solid-edge)]",
        ].join(" "),
        info: "border border-info/25 bg-info-soft text-fg",
        danger: "border border-danger/25 bg-danger-soft text-fg",
        success: "border border-success/25 bg-success-soft text-fg",
        caution: "border border-warning/30 bg-warning-soft text-fg",
        neutral: "bg-surface text-fg ring-1 ring-fg/10",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

type BannerTone = NonNullable<VariantProps<typeof banner>["tone"]>;

const LOUD_TONES: ReadonlySet<BannerTone> = new Set(["warning", "primary", "error"]);
/** Tones read out at once by screen readers; the rest are polite. */
const ALERT_TONES: ReadonlySet<BannerTone> = new Set(["danger", "error", "warning", "caution"]);

const BANNER_ICON: Readonly<Record<string, IconName>> = {
  warning: "alert-triangle",
  primary: "sparkles",
  info: "info",
  danger: "alert-circle",
  error: "alert-circle",
  success: "check-circle",
  caution: "alert-triangle",
  neutral: "info",
};

export type BannerProps = VariantProps<typeof banner> & {
  readonly title: string;
  readonly description?: ReactNode;
  readonly action?: ReactNode;
  readonly icon?: IconName;
  /** A slow wash of the banner's colour and the brand's behind it. For the one banner that matters. */
  readonly glow?: boolean;
  /** Lets the reader put the banner away for a while; the label says for how long ("Snooze for a day"). */
  readonly snooze?: { readonly label: string; readonly onSnooze: () => void };
};

// Each glow stays in its banner's own hue family; only the brand's blues mix
// with the blue and neutral tones, where they belong.
const GLOW_TOKENS: Readonly<Record<string, readonly string[]>> = {
  warning: ["warning-solid", "warning-glow-light", "warning-glow-deep", "warning-solid"],
  primary: ["inverted", "brand-navy", "brand-sky", "inverted"],
  info: ["info-soft", "brand-sky", "info", "info-soft"],
  danger: ["danger-soft", "danger", "danger-soft", "danger-soft"],
  success: ["success-soft", "success", "success-soft", "success-soft"],
  caution: ["warning-soft", "warning", "warning-soft", "warning-soft"],
  neutral: ["surface", "brand-sky", "brand-navy", "surface"],
};

export function Banner({ tone, title, description, action, icon, glow, snooze }: BannerProps) {
  const loud = LOUD_TONES.has(tone ?? "neutral");
  return (
    <div role={ALERT_TONES.has(tone ?? "neutral") ? "alert" : "status"} className={banner({ tone })}>
      {glow ? (
        tone === "error" ? (
          <AlarmFill />
        ) : (
          <GlowFill tokens={GLOW_TOKENS[tone ?? "neutral"] ?? []} />
        )
      ) : null}
      <span
        className={cn(
          "inline-flex size-8 shrink-0 items-center justify-center rounded-full [&_svg]:size-4",
          loud ? "bg-current/15" : "bg-surface ring-1 ring-border",
        )}
      >
        <Icon name={icon ?? BANNER_ICON[tone ?? "neutral"] ?? "info"} />
      </span>
      <div className="flex flex-1 flex-col gap-0.5">
        <p className="text-sm font-semibold">{title}</p>
        {description ? (
          <div className={cn("text-xs leading-5", loud ? "opacity-90" : "text-fg-muted")}>{description}</div>
        ) : null}
      </div>
      {action || snooze ? (
        <div className="flex shrink-0 items-center gap-2">
          {action ? <div data-slot="action">{action}</div> : null}
          {snooze ? (
            <button
              type="button"
              onClick={snooze.onSnooze}
              aria-label={snooze.label}
              title={snooze.label}
              className="inline-flex size-8 cursor-pointer items-center justify-center rounded-md opacity-70 transition-opacity hover:bg-current/10 hover:opacity-100 [&_svg]:size-4"
            >
              <Icon name="clock" />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export type EmptyStateProps = {
  readonly icon: IconName;
  readonly title: string;
  readonly description?: string;
  readonly action?: ReactNode;
};

export function EmptyState({ icon, title, description, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <span className="inline-flex size-12 items-center justify-center rounded-full bg-primary-soft text-primary-soft-fg [&_svg]:size-6">
        <Icon name={icon} />
      </span>
      <p className="text-base font-semibold text-fg">{title}</p>
      {description ? <p className="max-w-md text-sm leading-6 text-fg-muted">{description}</p> : null}
      {action ? <div className="pt-2">{action}</div> : null}
    </div>
  );
}

export function Spinner({ label }: { readonly label: string }) {
  return (
    <span role="status" className="inline-flex items-center gap-2 text-sm text-fg-muted [&_svg]:size-4">
      <Icon name="loader" spin />
      {label}
    </span>
  );
}

/** A placeholder block in the final shape of what is loading. */
export function Skeleton({ lines = 3 }: { readonly lines?: number }) {
  return (
    <div aria-hidden="true" className="flex flex-col gap-2.5">
      {Array.from({ length: lines }, (_, index) => (
        <div
          key={index}
          className={cn(
            "h-4 animate-pulse rounded-md bg-bg-subtle",
            index === lines - 1 ? "w-2/3" : "w-full",
          )}
        />
      ))}
    </div>
  );
}
